import { clientEnv } from "../env";
export type SupportCategory = "chat" | "voice" | "images" | "video" | "billing" | "account" | "other";
export type SupportStatus = "open" | "in_progress" | "waiting_user" | "resolved";
export interface SupportEntry {
  id: string; author: "user" | "support"; actorId: string; text: string; status: SupportStatus; createdAt: string;
}
export interface SupportTicket {
  id: string; userId: string; category: SupportCategory; title: string; status: SupportStatus;
  version: number; createdAt: string; updatedAt: string;
  diagnostics?: { appVersion: string; platform: string }; entries: SupportEntry[];
}
export type SupportSummary = Omit<SupportTicket, "entries" | "diagnostics">;
export interface SupportList {
  tickets: SupportSummary[]; hasMore: boolean; page: number; staff: boolean; durable: boolean;
}
export class SupportClientError extends Error {
  readonly status: number;
  constructor(status: number, message: string) { super(message); this.status = status; }
}
async function supportJson<T>(token: string, path: string, body?: unknown): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20_000);
  try {
    const response = await fetch(`${clientEnv.apiBaseUrl}/support/tickets${path}`, {
      method: body === undefined ? "GET" : "POST", signal: controller.signal,
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
    let payload;
    try { payload = await response.json(); }
    catch { throw new SupportClientError(response.status, "No se pudo leer la respuesta de soporte."); }
    if (!response.ok) throw new SupportClientError(response.status, payload.error ?? "No se pudo acceder al canal de incidencias.");
    return payload as T;
  } catch (error) {
    if (error instanceof SupportClientError) throw error;
    throw new SupportClientError(0, "No se pudo conectar. Conservamos el texto en pantalla; puedes reintentar el envío.");
  } finally { window.clearTimeout(timeout); }
}
export function listSupport(token: string, all = false, page = 0): Promise<SupportList> {
  return supportJson(token, `?all=${all ? "1" : "0"}&page=${page}`);
}
export function getSupport(token: string, id: string): Promise<{ ticket: SupportTicket }> {
  return supportJson(token, "/" + encodeURIComponent(id));
}
export interface CreateSupportInput {
  requestId: string; title: string; description: string; category: SupportCategory;
  diagnostics?: { appVersion: string; platform: string };
}
export function createSupport(token: string, body: CreateSupportInput): Promise<{ ticket: SupportTicket; durable: boolean }> {
  return supportJson(token, "", body);
}
export interface UpdateSupportInput { requestId: string; expectedVersion: number; message?: string; status?: SupportStatus; }
export function updateSupport(token: string, id: string, body: UpdateSupportInput): Promise<{ ticket: SupportTicket }> {
  return supportJson(token, "/" + encodeURIComponent(id), body);
}
